import _ from 'lodash'
import type {CreateExtArgs} from '@wix/document-manager-core'
import type {
    CompRef,
    Component,
    Pointers,
    RawComponentExportStructure,
    RawComponentExport,
    RawImportExportOptions,
    ComponentFeature,
    RawComponents,
    GlobalData,
    Dependencies,
    DependenciesBuilder,
    FixerVersions,
    Fragments,
    FragmentsOptions
} from '@wix/document-services-types'
import {deepClone} from '@wix/wix-immutable-proxy'
import {COMPONENT_FEATURES, UNNECESSARY_PROPS} from '../constants'
import {resolveOverrides} from './utils/overrides'
import {resolveDataItem} from './utils/dataItems'
import {getGlobalData} from './utils/globalData'
import {getComponentFeaturesByType, isSystemDataItem} from '../utils'
import {stripHashIfExists} from '../../../../utils/refArrayUtils'
import {getFixerVersions} from './utils/fixerVersions'
import {getDependenciesBuilder} from './utils/dependencies'

const getComponents = (rootPointer: CompRef, pointers: Pointers): CompRef[] =>
    [rootPointer, ...pointers.structure.getChildrenRecursively(rootPointer)] as CompRef[]

const restructureComponent = (component: Component): RawComponentExportStructure => ({structure: component, data: {}})

const sanitizeComponent = (component: RawComponentExportStructure) => {
    for (const property of UNNECESSARY_PROPS) {
        delete component.structure[property]
    }
}

const resolveDataItems = (
    extensionContext: CreateExtArgs,
    component: RawComponentExportStructure,
    pagePointer: CompRef,
    {componentFeatures}: RawImportExportOptions,
    dependenciesBuilder?: DependenciesBuilder
) => {
    const {pointers, extensionAPI} = extensionContext
    const features = getComponentFeaturesByType(component.structure.componentType, componentFeatures)
    _.forOwn(COMPONENT_FEATURES, ({query, namespace, alias}: ComponentFeature) => {
        const componentQuery = component.structure[query]
        if (!componentQuery) {
            return
        }

        delete component.structure[query]

        if (!features[alias]) {
            return
        }

        if (isSystemDataItem(componentQuery, namespace, extensionAPI)) {
            component.structure[query] = componentQuery
            return
        }

        const itemPointer = pointers.getPointer(stripHashIfExists(componentQuery), namespace)
        component.data[alias] = resolveDataItem(extensionContext, itemPointer, pagePointer, dependenciesBuilder)
    })
}

const serializeComponent = (
    extensionContext: CreateExtArgs,
    componentPointer: CompRef,
    pagePointer: CompRef,
    options: RawImportExportOptions,
    dependenciesBuilder?: DependenciesBuilder
): RawComponentExportStructure => {
    const {dal} = extensionContext
    const componentStructure = deepClone(dal.get(componentPointer))
    const rawComponent = restructureComponent(componentStructure)
    sanitizeComponent(rawComponent)
    resolveDataItems(extensionContext, rawComponent, pagePointer, options, dependenciesBuilder)
    resolveOverrides(extensionContext, rawComponent, componentPointer, pagePointer, options, dependenciesBuilder)

    return rawComponent
}

const serializeComponents = (
    extensionContext: CreateExtArgs,
    components: CompRef[],
    pagePointer: CompRef,
    dependenciesBuilder: DependenciesBuilder,
    options: RawImportExportOptions
): RawComponents => {
    const rawComponents = components.map((componentPointer: CompRef) =>
        serializeComponent(extensionContext, componentPointer, pagePointer, options, dependenciesBuilder)
    )

    return _.keyBy(rawComponents, comp => comp.structure.id)
}

const buildRawComponentExport = (
    rootPointer: CompRef,
    rawComponents: RawComponents,
    dependencies?: Dependencies,
    globalData?: GlobalData,
    fixerVersions?: FixerVersions
): RawComponentExport => {
    const rawComponentExport: RawComponentExport = {
        rootComponent: rootPointer.id,
        components: _.keyBy(rawComponents, 'structure.id')
    }

    if (dependencies !== undefined) {
        rawComponentExport.dependencies = dependencies
    }

    if (globalData !== undefined) {
        rawComponentExport.globalData = globalData
    }

    if (fixerVersions !== undefined) {
        rawComponentExport.fixerVersions = fixerVersions
    }

    return rawComponentExport
}

export const exportRawComponent = (
    extensionContext: CreateExtArgs,
    rootPointer: CompRef,
    options: RawImportExportOptions
): RawComponentExport => {
    const {pointers} = extensionContext
    const pagePointer = pointers.structure.getPageOfComponent(rootPointer) as CompRef
    const components: CompRef[] = getComponents(rootPointer, pointers)

    const dependenciesBuilder: DependenciesBuilder = getDependenciesBuilder(extensionContext, components)

    const rawComponents: RawComponents = serializeComponents(
        extensionContext,
        components,
        pagePointer,
        dependenciesBuilder,
        options
    )

    const fixerVersions: FixerVersions | undefined = getFixerVersions(extensionContext, pagePointer)
    const globalData: GlobalData | undefined = getGlobalData(extensionContext, rootPointer, dependenciesBuilder)
    const dependencies: Dependencies | undefined = dependenciesBuilder.build()

    return buildRawComponentExport(rootPointer, rawComponents, dependencies, globalData, fixerVersions)
}

export const exportFragments = (extensionContext: CreateExtArgs, options?: FragmentsOptions): Fragments => {
    const {coreConfig, dal} = extensionContext
    const result: Fragments = {}
    const supportedFragments = coreConfig.supportedFragments ?? {}

    for (const fragmentType of Object.keys(supportedFragments)) {
        const fragmentOptions = options?.[fragmentType]
        const namespaces = dal.getNamespacesInFragment(fragmentType)

        for (const namespace of namespaces) {
            const items = dal.getItemsInNamespace(fragmentType, namespace).map(pointer => dal.get(pointer))

            for (const item of items) {
                const {id, metaData} = item
                const {fragmentInstance} = metaData

                if (fragmentOptions?.length && !fragmentOptions.includes(fragmentInstance)) {
                    continue
                }

                result[fragmentType] ??= {}
                result[fragmentType][fragmentInstance] ??= {}
                result[fragmentType][fragmentInstance][namespace] ??= {}
                result[fragmentType][fragmentInstance][namespace][id] = item
            }
        }
    }

    return result
}
